import i18next from 'i18next';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import en from '../../../i18n/en-us.json';
import pt from '../../../i18n/pt-br.json';
import es from '../../../i18n/es-es.json';
import { MachineModel as Model } from '../../../model/machine.model';
import { ProcessLinkService as Service } from '../../../services/process-link.service';
import { Autocomplete, TextField } from '@mui/material';
import { ProcessesService } from 'app/processes/services/processes.service';
import { useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap'
import ITSVG from 'common/helpers/ITSVG';
import closeModalIcon from 'assets/images/icons/close.svg'
import SaveButton from 'common/partials/SaveButton';
import { ProcessModel } from 'app/processes/model/process.model';

interface AddProcessModalProps {
  open: boolean;
  handleClose: () => void;
  handleFetchData: () => void;
}

export function AddProcessesLink(props: AddProcessModalProps) {
  i18next.addResourceBundle('us', 'translation', en);
  i18next.addResourceBundle('br', 'translation', pt);
  i18next.addResourceBundle('es', 'translation', es);

  const { t } = useTranslation();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);

  let initialData: Model = {
    id: 0,
    machineId: parseInt(id ?? '0'),
    isActive: true,
  };

  const formSchema = Yup.object().shape({
    processId: Yup.string()
      .required(t('crud.validators.requiredField')),
  });

  const [data] = useState<Model>(initialData);
  const [processes, setProcesses] = useState<ProcessModel[]>([]);

  const formik = useFormik<Model>({
    enableReinitialize: true,
    initialValues: data,
    validationSchema: formSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        const body: Model = {
          id: data.id,
          name: values.name,
          machineId: data.machineId,
          processId: values.processId,
          isActive: values.isActive,
        };
        const result = await Service.postData(body);
        if (result && !result?.hasErrors) {
          toast.success(`${t('crud.create.successMessage')}`);
        } else {
          toast.error(`${t('crud.create.errorMessage')}`);
        }
      } catch (error) {
        toast.error(`${t('crud.create.errorMessage')}`);
      } finally {
        props.handleClose();
        props.handleFetchData();
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchProcesses = async () => {
      if (id) {
        const response = await ProcessesService.getDataList();
        if (response && !response.hasErrors) {
          setProcesses(response.data.items);
        };
      }
    }
    if (props.open) {
      fetchProcesses();
    }

    return () => {
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  return (
    <Modal
      show={props.open}
      onHide={props.handleClose}
      dialogClassName='modal-dialog-centered mw-600px h-auto'
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h3>{t('addProcess')}</h3>
          <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={props.handleClose}>
            <ITSVG path={closeModalIcon} className='svg-icon-1' />
          </div>
        </div>
        <form onSubmit={formik.handleSubmit}>
          <div className="modal-body scroll-y mx-5 my-5">
            <div className="row">
              <div className="col-md-12">
                <label className="required form-label">{t('form.label.name')}</label>
                <Autocomplete
                  id="processId"
                  options={processes}
                  getOptionLabel={option => option.name ? option.name : ""}
                  onChange={(_, value) => formik.setFieldValue("processId", value?.id)}
                  onInputChange={(_, value) => {
                    if (!value) {
                      formik.setFieldValue("processId", undefined)
                    }
                  }}
                  onBlur={formik.handleBlur}
                  renderInput={(params) =>
                    <TextField
                      {...params}
                      placeholder={`${t('generalMessages.select')}`}
                    />
                  }
                />
                {formik.errors.processId && (
                  <div className="mt-3 text-danger fw-bold">{formik.errors.processId}</div>
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer d-flex justify-content-end py-6 px-9">
            <button type="button" className="btn btn-light btn-active-light-primary me-2" onClick={props.handleClose}>
              {t('crud.create.buttonCancel')}
            </button>
            <SaveButton type="submit" loading={isLoading} />
          </div>
        </form>
      </div>
    </Modal>
  );
}
